import React, { useCallback, useEffect, useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import { additionalInformation } from "us.collection.debtor/actions";
import Common from "us.common";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import { RootState } from "us.helper/types";
import { PlusOutlined, DeleteOutlined } from "us.icons";
import { SaveInformation } from "us.collection.debtor/components/DebtorBasicInfo/Components/AdditionalInformation/Repository";
import { AdditionalInfoValidationSchema } from "us.collection.debtor/components/DebtorBasicInfo/Components/AdditionalInformation/Validations";
import { AdditionalInfoDetailsProps } from "us.collection.debtor/components/DebtorBasicInfo/Components/AdditionalInformation/Interfaces";
import {
  getAdditionalInfoInitFormValues,
  convertValueToProperFormat,
  getValueDataType,
  getDefaultValue,
} from "us.collection.debtor/components/DebtorBasicInfo/Components/AdditionalInformation/Functions";
import { DrawerType } from "us.collection.debtor/constants/AdditionalInformation";
import { InformationRecord } from "us.collection.debtor/Interfaces";
import {
  AdditionalInfoDataTypes,
  EntityType,
  ValueType,
} from "us.common/constants";
import {
  getValueComponent,
  getExtendedFieldValueSource,
  getSelectedMetaDataInfo,
  getDynamicPlaceholderValue,
} from "us.common/functions";

const {
  $Tooltip,
  $Button,
  $Row,
  $Col,
  $Select,
  $Popconfirm,
  $DynamicComponent,
} = Common.Components;

/**
 * @description - Additional information detail view component
 * @link Design Document - https://unicorn-solutions.atlassian.net/browse/UBX4-23262
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 04/11/2024
 */
const AdditionalInfoDetails: React.FC<
  AdditionalInfoDetailsProps & PropsFromRedux
> = (props) => {
  const { t } = useTranslation(["US.COLLECTION.DEBTOR"]);

  const {
    informationList,
    metaData,
    drawerInfo,
    isInformationSaving,
    infoMetaData,
    bmdDataTypeValues,
    onCancel,
    newAdditionalInformation,
    updateAdditionalInformation,
    getInfoMetaData,
    getBmdDataTypeValues,
  } = props;

  const overLayElement: HTMLElement = document.getElementById(
    "hiddenOverlay"
  ) as HTMLElement;

  useEffect(() => {
    getInfoMetaData &&
      getInfoMetaData({
        category: EntityType.DEBTOR,
      });
  }, []);

  const isEditable = useMemo(() => {
    return (
      drawerInfo?.isEditable ||
      drawerInfo?.type == DrawerType.NEW_ADDITIONAL_INFO
    );
  }, [drawerInfo]);

  const initialFormValues = useMemo(() => {
    const initialValues = getAdditionalInfoInitFormValues(
      drawerInfo?.infoId,
      informationList?.data
    );
    return initialValues;
  }, [drawerInfo?.infoId, informationList]);

  const getDataTypeValues = useCallback(
    (dataTypeId: any) => {
      return bmdDataTypeValues[dataTypeId]?.data ?? [];
    },
    [bmdDataTypeValues, infoMetaData]
  );

  const isDatTypeValuesLoading = useCallback(
    (key: any) => {
      const metaData = getSelectedMetaDataInfo(key, infoMetaData.infoOptions);
      return bmdDataTypeValues[metaData?.dataTypeId]?.isLoading ?? false;
    },
    [bmdDataTypeValues, infoMetaData.infoOptions]
  );

  const getOptionList = useCallback(
    (listKey: any) => {
      const metaData = getSelectedMetaDataInfo(
        listKey,
        infoMetaData.infoOptions
      );
      if (metaData) {
        const dataTypeValues = getDataTypeValues(metaData.valueTypeId);
        const valueSource = getExtendedFieldValueSource(
          listKey,
          infoMetaData.infoOptions,
          dataTypeValues
        );
        return valueSource;
      }
      return [];
    },
    [getDataTypeValues, infoMetaData]
  );

  /**
   * @description Handle submit button action
   * @param {any} value
   * @param {any} option
   * @param {any} setFieldValue
   * @param {any} setFieldTouched
   * @param {any} index
   */
  const handleSelect = (
    value: any,
    option: any,
    setFieldValue: any,
    setFieldTouched: any,
    index: any
  ) => {
    const metaData = getSelectedMetaDataInfo(value, infoMetaData.infoOptions);
    if (metaData?.valueType == ValueType.SP) {
      getBmdDataTypeValues &&
        getBmdDataTypeValues({
          dataTypeId: metaData?.valueTypeId,
        });
    }
    setFieldValue(
      `informationList[${index}].value`,
      getDefaultValue(option?.value, infoMetaData.infoOptions)
    );
    setTimeout(() => {
      setFieldTouched(`informationList[${index}].key`, true);
    }, 50);
  };

  /**
   * @Description Add new information
   */
  const handleAdd = (setValues: any) => {
    setValues(
      (preValues: any) => ({
        ...preValues,
        informationList: [
          ...preValues?.informationList,
          { key: undefined, value: undefined },
        ],
      }),
      false
    );
  };

  /**
   * @Description Delete information
   */
  const handleDelete = (
    informationData: any[],
    infoIndex: number,
    setFieldValue: any
  ) => {
    let newInformationList = [...informationData];
    if (newInformationList?.length > 1) {
      newInformationList?.splice(infoIndex, 1);
      setFieldValue("informationList", newInformationList);
    }
  };

  /**
   * @description Handle submit button action
   * @param {any} values
   */
  const handleSubmit = (values: any) => {
    const saveParams = SaveInformation.call({
      ...values,
      debtorNo: metaData.data?.debtorEntNumber,
    });

    if (drawerInfo?.type === DrawerType.NEW_ADDITIONAL_INFO) {
      newAdditionalInformation && newAdditionalInformation(saveParams);
    } else {
      updateAdditionalInformation &&
        updateAdditionalInformation({ saveParams, infoId: drawerInfo?.infoId });
    }
  };

  /**
   * Retrieves the placeholder value for a given key based on its data type and metadata.
   *
   * @param key - The key for which the placeholder value is to be determined.
   * @returns The translated placeholder value corresponding to the key's data type.
   *
   * The function determines the data type of the key using `getValueDataType` and
   * retrieves a dynamic placeholder value using `getDynamicPlaceholderValue`. The
   * placeholder is then translated using the `t` function.
   */
  const getPlaceholderValue = (key: any) => {
    const dataType = getValueDataType(key, infoMetaData.infoOptions);
    const placeholder = getDynamicPlaceholderValue(dataType);
    return t(placeholder);
  };

  const onOpenSelectOptions = (key: any) => {
    const metaData = getSelectedMetaDataInfo(key, infoMetaData.infoOptions);
    if (metaData?.valueType == ValueType.SP) {
      getBmdDataTypeValues &&
        getBmdDataTypeValues({
          dataTypeId: metaData?.valueTypeId,
        });
    }
  };

  return (
    <Formik
      initialValues={initialFormValues}
      enableReinitialize
      validateOnMount
      validateOnChange
      validateOnBlur
      validationSchema={AdditionalInfoValidationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        isSubmitting,
        isValid,
        handleSubmit,
        setValues,
        setFieldValue,
        setFieldTouched,
      }) => (
        <div className="additional-info" id="hiddenOverlay">
          <div className="d-flex flex-column mb-3">
            <label className="ant-form-item-required">
              {t("US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.STATUS")}
            </label>
            <$Tooltip
              placement="topLeft"
              title={!isEditable ? values?.status : ""}
            >
              <$Select
                name="status"
                placeholder={t(
                  "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.STATUS"
                )}
                options={infoMetaData?.statusOptions}
                size="small"
                allOption={false}
                virtual={false}
                disabled={!isEditable || infoMetaData?.isLoading}
                loading={infoMetaData?.isLoading}
                onSearchBy={["label"]}
              />
            </$Tooltip>
          </div>

          <label className="ant-form-item-required">
            {t("US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.INFORMATION")}
          </label>
          {values?.informationList?.map(
            (infoRecord: InformationRecord, index: number) => (
              <div className="info-item-container" key={index}>
                <div className="info-content-items">
                  <$Row gutter={20} className="align-content-center w-100">
                    <$Col span={10}>
                      <$Tooltip
                        placement="topLeft"
                        title={!isEditable ? infoRecord?.key : ""}
                      >
                        <$Select
                          name={`informationList[${index}].key`}
                          formitem={{}}
                          placeholder={t(
                            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.HEADER"
                          )}
                          options={infoMetaData?.infoOptions}
                          size="small"
                          allOption={false}
                          virtual={false}
                          disabled={!isEditable || infoMetaData?.isLoading}
                          onSearchBy={["label"]}
                          getPopupContainer={() => overLayElement}
                          className="w-100"
                          onSelect={(value: any, option: any) => {
                            handleSelect(
                              value,
                              option,
                              setFieldValue,
                              setFieldTouched,
                              index
                            );
                          }}
                        />
                      </$Tooltip>
                    </$Col>
                    <$Col span={14}>
                      <$Tooltip
                        placement="topLeft"
                        title={
                          !isEditable &&
                          getValueDataType(
                            values?.informationList[index].key,
                            infoMetaData?.infoOptions
                          ) != AdditionalInfoDataTypes.DATE_TIME
                            ? infoRecord?.value
                            : ""
                        }
                      >
                        <span>
                          <$DynamicComponent
                            name={`informationList[${index}].value`}
                            component={getValueComponent(
                              values?.informationList[index].key,
                              infoMetaData?.infoOptions
                            )}
                            value={convertValueToProperFormat(
                              values?.informationList[index],
                              infoMetaData?.infoOptions
                            )}
                            className={
                              getValueDataType(
                                values?.informationList[index].key,
                                infoMetaData?.infoOptions
                              ) != AdditionalInfoDataTypes.BOOLEAN
                                ? "w-100"
                                : ""
                            }
                            placeholder={getPlaceholderValue(
                              values?.informationList[index].key
                            )}
                            required
                            tabIndex={4}
                            options={getOptionList(
                              values?.informationList[index].key
                            )}
                            allOption={false}
                            virtual={false}
                            onSearchBy={["label"]}
                            disabled={!isEditable || infoMetaData.isLoading}
                            getPopupContainer={() => overLayElement}
                            onDropdownVisibleChange={(open: boolean) => {
                              open &&
                                onOpenSelectOptions(
                                  values?.informationList[index].key
                                );
                            }}
                            loading={isDatTypeValuesLoading(
                              values?.informationList[index].key
                            )}
                            maxLength={
                              200
                            }
                            precision={
                              getValueDataType(
                                values?.informationList[index].key,
                                infoMetaData.infoOptions
                              ) === AdditionalInfoDataTypes.INT
                                ? 0
                                : undefined
                            }
                          />
                        </span>
                      </$Tooltip>
                    </$Col>
                  </$Row>
                </div>
                {isEditable && (
                  <div
                    className="d-flex align-items-center justify-content-end"
                    style={{ width: "90px" }}
                  >
                    <div className="d-flex h-100 align-items-center">
                      <$Popconfirm
                        title={t("COMMON.SURE_TO_DELETE_?")}
                        placement="topLeft"
                        onConfirm={() => {
                          handleDelete(
                            values?.informationList,
                            index,
                            setFieldValue
                          );
                        }}
                      >
                        <$Tooltip
                          placement="topLeft"
                          title={t(
                            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.DELETE_INFORMATION"
                          )}
                        >
                          <div
                            className="info-delete-btn"
                            hidden={values?.informationList?.length == 1}
                          >
                            <DeleteOutlined className="text-error" />
                          </div>
                        </$Tooltip>
                      </$Popconfirm>
                      {values?.informationList?.length - 1 == index && (
                        <$Tooltip
                          placement="topLeft"
                          title={t(
                            "US.COLLECTION.DEBTOR:ADDITIONAL_INFORMATION.ADD_NEW_INFORMATION"
                          )}
                        >
                          <div
                            className="add-btn"
                            onClick={() => handleAdd(setValues)}
                          >
                            <PlusOutlined className="add-btn-text" />
                          </div>
                        </$Tooltip>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              {isEditable && (
                <$Popconfirm
                  title={t("COMMON.SURE_TO_PROCEED_?")}
                  placement="topLeft"
                  data-testid="pop-confirm"
                  okButtonProps={{
                    dataTestid: "pop-confirm-ok",
                  }}
                  onConfirm={() => handleSubmit()}
                  okText={t("COMMON.YES")}
                  cancelText={t("COMMON.NO")}
                  disabled={isInformationSaving || isSubmitting || !isValid}
                >
                  <$Button
                    className="ml-3 mr-2"
                    type="primary"
                    data-testid="save-btn"
                    disabled={isInformationSaving || isSubmitting || !isValid}
                    loading={isInformationSaving || isSubmitting}
                  >
                    {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                  </$Button>
                </$Popconfirm>
              )}
              <$Button onClick={() => onCancel()}>
                {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
              </$Button>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

const mapState = (state: RootState) => {
  const { common, domainView, additionalInformation } = state;
  const {
    informationList,
    drawerInfo,
    isInformationSaving,
    infoMetaData,
    bmdDataTypeValues,
  } = additionalInformation;
  const { metaData } = domainView;
  return {
    metaData,
    currentLanguage: common.currentLanguage,
    informationList,
    drawerInfo,
    isInformationSaving,
    infoMetaData,
    bmdDataTypeValues,
  };
};

const {
  newInformation,
  informationDetails,
  informationMetaData,
  bmdDataTypeValues,
} = additionalInformation;
const mapDispatch = {
  newAdditionalInformation: newInformation.save,
  updateAdditionalInformation: informationDetails.update,
  getInfoMetaData: informationMetaData.get,
  getBmdDataTypeValues: bmdDataTypeValues.get,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AdditionalInfoDetails);
